import { useState } from 'react'
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import type { AppSettings } from '../types.js'

interface Props {
  settings: AppSettings
  onSave: (next: AppSettings) => void
  onClose: () => void
  onPing: () => Promise<{ ok: boolean; message: string }>
}

export function SettingsCard({ settings, onSave, onClose, onPing }: Props) {
  const [safewardUrl, setSafewardUrl] = useState(settings.safewardUrl)
  const [safewardAuthToken, setToken] = useState(settings.safewardAuthToken)
  const [autoRefreshSeconds, setAuto] = useState(settings.autoRefreshSeconds)
  const [msg, setMsg] = useState<{ tone: 'ok' | 'err'; text: string } | null>(null)

  const save = () => {
    onSave({
      // moduleSafe is URL-only state, pass through unchanged.
      moduleSafe: settings.moduleSafe,
      safewardUrl: safewardUrl.trim().replace(/\/+$/, ''),
      safewardAuthToken: safewardAuthToken.trim(),
      autoRefreshSeconds: Math.max(0, Number(autoRefreshSeconds || 0)),
    })
    setMsg({ tone: 'ok', text: 'saved' })
  }

  const ping = async () => {
    const r = await onPing()
    setMsg({ tone: r.ok ? 'ok' : 'err', text: r.message })
  }

  return (
    <Card className="mb-4">
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle>Settings</CardTitle>
        <Button variant="ghost" size="sm" onClick={onClose}>
          Close
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          The Safe address lives in the URL — change it via{' '}
          <code className="font-mono">?safe=0x…</code> or by going back to the{' '}
          <a href="?" className="text-sky-400 hover:underline">
            landing page
          </a>
          .
        </p>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="safeward-url">SafeWard URL</Label>
            <Input
              id="safeward-url"
              value={safewardUrl}
              onChange={(e) => setSafewardUrl(e.target.value)}
              placeholder="leave blank for same-origin"
              className="font-mono"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="safeward-auth">SafeWard auth token</Label>
            <Input
              id="safeward-auth"
              type="password"
              value={safewardAuthToken}
              onChange={(e) => setToken(e.target.value)}
              placeholder="bearer token (only needed for direct Worker calls)"
              className="font-mono"
            />
            <p className="text-xs text-muted-foreground">
              In prod the Pages Function injects this server-side.
            </p>
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="auto-refresh">Auto-refresh (seconds, 0 to disable)</Label>
          <Input
            id="auto-refresh"
            type="number"
            value={autoRefreshSeconds}
            onChange={(e) => setAuto(Number(e.target.value))}
            className="font-mono"
          />
        </div>
      </CardContent>
      <CardFooter className="flex flex-wrap items-center gap-2">
        <Button onClick={save}>Save</Button>
        <Button variant="outline" onClick={() => void ping()}>
          Ping SafeWard
        </Button>
        {msg && (
          <span
            className={
              msg.tone === 'ok'
                ? 'text-sm text-emerald-400'
                : 'text-sm text-destructive'
            }
          >
            {msg.tone === 'ok' ? '✓' : '✗'} {msg.text}
          </span>
        )}
      </CardFooter>
    </Card>
  )
}
